import {
  getShowOOPModalEvent,
  getClickCancelOOPModalEvent,
  getClickContinueOOPModalEvent,
  useShowPolicyBanner,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import GoogleMap, { LatLng, LatLngBounds } from "google-maps-react-markers";
import {
  ActionButton,
  HotelAvailabilityMapTooltip,
  Icon,
  IconName,
  OutOfPolicyModal,
  useDeviceTypes,
} from "halifax";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  BoundingBox,
  buildEncodedPersistentLodgingTrackingProperties,
  CLICKED_LODGING_MAP,
  ClickedLodgingMapEnum,
  ClickedLodgingMapProperties,
  CorpLodging,
  getLodgingTrackingProperties,
  HotelDetailsEntrySourceEnum,
  HOTEL_LIST_MAP_CAROUSEL,
  IIdLodgings,
  IResult,
  LocationDescriptorEnum,
  Lodging,
  LodgingCollectionEnum,
  LodgingLocation,
  LodgingSelectionEnum,
  MapFunnelEnum,
  ModalScreens,
  SEARCH_THIS_AREA,
  ViewedCorpRateDescriptorEntryPoints,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import useSupercluster from "use-supercluster";
import { v4 as uuidv4 } from "uuid";
import { localCache } from "@capone/common";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../App";
import {
  AVAILABILITY_CLUSTER_MAP,
  AVAILABILITY_CLUSTER_MAP_MARKER_LIMIT,
  AVAILABILITY_SEARCH_BOUNDING_BOX,
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  CLUSTER,
  CLUSTER_MAP_MARKER_LIMIT_VARIANTS,
  CLUSTER_MAP_VARIANTS,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  LC_GATING_EXPERIMENT,
  LIMIT_200,
  LIMIT_30,
  LIMIT_50,
  LODGING_PROMOTIONS,
  LODGING_PROMOTIONS_AVAILABLE,
  LODGING_PROMOTIONS_VARIANTS,
  LUXURY_PAY_AT_HOTEL_BADGE,
  ALLOWED_PAY_LATER_LODGINGS,
  AllowedPayLaterLodgingsConfig,
  getExperimentVariantJSON,
  PREMIER_COLLECTION_EXPERIMENT,
  SEARCH_BOUNDING_BOX_VARIANTS,
  STAYS_SEARCH,
  useExperiments,
} from "../../../../context/experiments";
import { isAllowedPayLaterLodging } from "../../../../utils/payLater";
import { onOpenCompareBarTooltip } from "../../../../utils/events";
import {
  PATH_SHOP,
  PATH_VACATION_RENTAL_SHOP,
  PREMIER_COLLECTION_PATH_SHOP,
  PREMIER_PATH_VACATION_RENTALS_SHOP,
} from "../../../../utils/paths";
import { transformToStringifiedQuery } from "../../../shop/utils/queryStringHelpers";
import { WishlistToggleHeart } from "../../../wishlist/components/WishlistToggleHeart/component";
import { HotelAvailabilityCallState } from "../../reducer/state";
import { MobileViewHotelsNearSearch } from "../AvailabilityMap/components/MobileViewHotelsNearSearch";
import { ViewHotelsNearLocationAutocomplete } from "../ViewHotelsNearLocationAutocomplete";
import { ClusterMarker } from "./components/ClusterMarker";
import { ListingMarker } from "./components/ListingMarker";
import { LocationMarker } from "./components/LocationMarker";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM_LEVEL,
  MAP_STYLE,
  Z_INDEX_BASE_LISTING_MARKER,
  Z_INDEX_CLUSTER_MARKER,
  Z_INDEX_HOVER_OR_CLICKED_CLUSTER_MARKER,
  Z_INDEX_HOVER_OR_SELECTED_LISTING_MARKER,
  Z_INDEX_VIEW_STAYS_NEAR_MARKER,
} from "./const";
import { AvailabilityClusterMapConnectorProps } from "./container";
import "./styles.scss";
import {
  ClusterProperties,
  SuperclusterCluster,
  SuperclusterPoint,
} from "./types";
import { openHotelDetailsPage } from "./utils";

export interface IAvailabilityClusterMap
  extends AvailabilityClusterMapConnectorProps,
    RouteComponentProps {
  isWishlistMode?: boolean;
}

export const AvailabilityClusterMap = (props: IAvailabilityClusterMap) => {
  const {
    lodgings, // The lodgings returned from the avilability polling (initial & followup)
    availabilityCallState, // The state of the current availability call (initial, followup, complete, error)
    listHoveredLodgingId, // The lodging being hovered from the availability list
    listSelectedLodgingId, // The lodging selected from the availability list
    fetchInitialHotelAvailability, // Performs the initial search for stays availability
    history,

    /**
     * The following props are needed for the map marker modals.
     */
    isVentureX,
    searchedNightCount,
    accountReferenceId,
    largestValueAccount,

    /**
     * The following props are used to determine what kind of search was performed:
     *
     * - Search from the availability list searchbar
     * - "Search this area" button on the map
     * - "View stays near" autocomplete input on the map
     */
    searchLocation, // The location of the search (updates in all cases)
    searchCentroids, // The centroids of the search result (unique to each availability call)
    searchBoundingBox, // The search bounding box of the search result (unique to each availability call)
    searchMapBound, // The map bounds set by clicking "Search this area" on the map.
    viewHotelsNearLocation, // The location set by using the "View stays near" autocomplete input on the map.
    setSearchMapBound, // Sets the map bounds which is used by fetchInitialHotelAvailability

    /**
     * The following props are required for the map "View stays near" autocomplete
     */
    fetchViewHotelsNearLocationCategories, // Fetches the categories for the autocomplete
    viewHotelsNearAvailabilityProperties, // Needed for the tracking properties
    setViewHotelsNearLocation, // Sets the location for the "View stays near" autocomplete input for mobile

    /**
     * The following props are required to open the hotel details page (openHotelDetailsPage)
     */
    searchLocationResult,
    hotelQueryParams,
    sortOrder,
    mapBounds, // Current bounds of the map
    roomsCount, // The number of rooms from the search
    viewedHotelListProperties, // Tracking properties

    /**
     * The following props are used to determine if the map actions should be hidden
     */
    isWishlistMode,
  } = props;

  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const [showPolicyModal, setShowPolicyModal] = useState<string | null>(null);

  // Experiments
  const expState = useExperiments();
  const clusterMapVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_CLUSTER_MAP,
    CLUSTER_MAP_VARIANTS
  );
  const showClustersVariant = clusterMapVariantExp === CLUSTER;

  const searchBoundingBoxVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_SEARCH_BOUNDING_BOX,
    SEARCH_BOUNDING_BOX_VARIANTS
  );

  const useSearchBoundingBox = searchBoundingBoxVariantExp === AVAILABLE;
  /**
   * This experiment only applies when clusterMapVariantExp === NO_CLUSTER
   *
   * It limits the amount of markers to show on the map viewport
   */
  const markerLimitVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_CLUSTER_MAP_MARKER_LIMIT,
    CLUSTER_MAP_MARKER_LIMIT_VARIANTS
  );

  let noClusteringMarkerLimit = 999;
  switch (markerLimitVariantExp) {
    case LIMIT_200:
      noClusteringMarkerLimit = 200;
      break;
    case LIMIT_50:
      noClusteringMarkerLimit = 50;
      break;
    case LIMIT_30:
      noClusteringMarkerLimit = 30;
      break;
    default:
      noClusteringMarkerLimit = 999;
  }

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const shouldShowPcStyling = (lodging: Lodging) => {
    return (
      isPremierCollectionEnabled &&
      lodging.lodgingCollection === LodgingCollectionEnum.Premier
    );
  };

  const shouldShowLcStyling = (lodging: Lodging) => {
    return (
      (isPremierCollectionEnabled || isLifestyleCollectionEnabled) &&
      lodging.lodgingCollection === LodgingCollectionEnum.Lifestyle
    );
  };

  const lodgingPromotions = getExperimentVariantCustomVariants(
    expState.experiments,
    LODGING_PROMOTIONS,
    LODGING_PROMOTIONS_VARIANTS
  );

  const isLodgingPromotionsExperiment =
    lodgingPromotions === LODGING_PROMOTIONS_AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isPayAtHotelBadgeEnabled =
    getExperimentVariant(expState.experiments, LUXURY_PAY_AT_HOTEL_BADGE) ===
    AVAILABLE;

  const allowedPayLaterLodgingIds = React.useMemo(() => {
    const config = getExperimentVariantJSON(
      expState.experiments,
      ALLOWED_PAY_LATER_LODGINGS
    ) as AllowedPayLaterLodgingsConfig | null;
    return config?.data?.lodgingIds;
  }, [expState.experiments]);

  // Initializing Map
  const mapRef = useRef<any>(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false); // Ensures the Google map is initialized with the API key
  const [centerCoordsLoaded, setCenterCoordsLoaded] = useState(false); // Ensures the center is parsed from the searchLocation and map pans
  const [initialMapLoaded, setInitialMapLoaded] = useState(false); // Shows the loading skeleleton while map variables are loading
  const [mapMoved, setMapMoved] = useState(false); // Shows the loading skeleleton while map variables are loading
  const [initialSearch, setInitialSearch] = useState(true); // Tracking property. If "Search this area" hasn't been clicked or "View stays near..." has not been used.

  const [currSearchLocation, setCurrSearchLocation] =
    useState<LodgingLocation | null>();
  const [currSearchMapBound, setCurrSearchMapBound] = useState<BoundingBox>();
  const [currViewHotelsNearLocation, setCurrViewHotelsNearLocation] =
    useState<IResult>();

  const [
    mapAutocompleteSearchCoordinates,
    setMapAutocompleteSearchCoordinates,
  ] = useState<LatLng | undefined>(undefined);

  const onGoogleApiLoaded = ({ map }: any) => {
    mapRef.current = map;
    setGoogleMapsLoaded(true);

    // Handling the clean/dirty state of the map in a listener because the map.onChange handler is instance scoped
    map.addListener("drag", () => {
      if (!mapMoved) {
        setMapMoved(true);
      }
    });
  };

  useEffect(() => {
    if (googleMapsLoaded && centerCoordsLoaded) {
      setInitialMapLoaded(true);
    }
  }, [googleMapsLoaded, centerCoordsLoaded]);

  // Map Markers
  const [bounds, setBounds] = useState<any>([]);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);
  const [hoveredMarker, setHoveredMarker] = useState<string>(); // Used to set the z-index of the hovered marker
  const [hoveredCluster, setHoveredCluster] = useState<number>(); // Used to set the z-index of the hovered cluster
  const [selectedMarker, setSelectedMarker] = useState<string>();
  const [selectedMarkerLodging, setSelectedMarkerLodging] = useState<Lodging>(); // Used for the mobile map tooltip
  const [
    viewHotelsNearMobileSearchOpened,
    setViewHotelsNearMobileSearchOpened,
  ] = React.useState(false);

  // Needed for mobile map modal
  const { policies, isAutoApprovalEnabled } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  const moveMapTo = (lat: number, lng: number, zoom?: number) => {
    if (!mapRef?.current) return;
    if (zoom != null) {
      mapRef.current.setZoom(zoom);
    }
    mapRef.current.panTo({
      lat: lat,
      lng: lng,
    });
  };

  const moveMapToBounds = (bounds: LatLngBounds) => {
    if (!mapRef?.current) return;
    if (zoom != null) {
      mapRef.current.setZoom(zoom);
    }
    mapRef.current.panToBounds(bounds);
  };

  // Set the selected marker when a listing is selected from the list
  useEffect(() => {
    if (listSelectedLodgingId) {
      setSelectedMarker(listSelectedLodgingId);
    }
  }, [listSelectedLodgingId]);

  /**
   * This effect is used to determine the location of the map.
   */
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    if (
      searchMapBound &&
      currSearchMapBound?.northEast !== searchMapBound?.northEast &&
      currSearchMapBound?.southWest !== searchMapBound?.southWest
    ) {
      // Rare case where the user switches from mobile -> desktop
      if (!centerCoordsLoaded) {
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(
            searchMapBound?.southWest?.lat,
            searchMapBound?.southWest?.lon
          ),
          new google.maps.LatLng(
            searchMapBound?.northEast?.lat,
            searchMapBound?.northEast?.lon
          )
        );
        moveMapToBounds(bounds);
      }

      setMapMoved(false);
      setInitialSearch(false); // The initial search has changed
      setSelectedMarker(undefined); // Reset the selected marker when a new location is selected
      setCenterCoordsLoaded(true);
      setCurrSearchMapBound(searchMapBound);
    } else if (
      viewHotelsNearLocation &&
      currViewHotelsNearLocation?.id !== viewHotelsNearLocation?.id
    ) {
      const geocoder = new google.maps.Geocoder();
      geocoder
        .geocode({
          placeId: (viewHotelsNearLocation.id as IIdLodgings).lodgingSelection
            .placeId,
        })
        .then(({ results }) => {
          setMapAutocompleteSearchCoordinates(results[0].geometry.location);
          moveMapTo(
            results[0].geometry.location.lat(),
            results[0].geometry.location.lng(),
            16
          );
          setMapMoved(false);
          setInitialSearch(false); // The initial search has changed
          setSelectedMarker(undefined); // Reset the selected marker when a new location is selected
          setCurrViewHotelsNearLocation(viewHotelsNearLocation);
          setCenterCoordsLoaded(true);

          trackEvent({
            eventName: "view_hotels_near",
            properties: {
              ...viewHotelsNearAvailabilityProperties,
              map_funnel: MapFunnelEnum.STAYS,
            },
          });
          fetchInitialHotelAvailability({
            history: history,
            searchFromMap: false,
            searchHotelsNear: true,
            includeHomes: staysSearchEnabled,
          });
        });
    } else if (
      !searchMapBound &&
      !viewHotelsNearLocation &&
      searchCentroids &&
      currSearchLocation?.coordinates?.lat !==
        searchLocation?.coordinates?.lat &&
      currSearchLocation?.coordinates?.lon !== searchLocation?.coordinates?.lon
    ) {
      if (useSearchBoundingBox && searchBoundingBox) {
        mapRef.current.fitBounds(
          new google.maps.LatLngBounds(
            {
              lat: searchBoundingBox.southWest.lat,
              lng: searchBoundingBox.southWest.lon,
            },
            {
              lat: searchBoundingBox.northEast.lat,
              lng: searchBoundingBox.northEast.lon,
            }
          )
        );
      } else {
        const lat = searchCentroids?.lat || 0;
        const lng = searchCentroids?.lon || 0;

        moveMapTo(lat, lng, DEFAULT_ZOOM_LEVEL);
      }
      setMapMoved(false);
      setSelectedMarker(undefined);
      setCurrSearchLocation(searchLocation);
      setCenterCoordsLoaded(true);
    }
  }, [
    centerCoordsLoaded,
    searchCentroids,
    searchLocation,
    currSearchLocation,
    searchMapBound,
    currSearchMapBound,
    viewHotelsNearLocation,
    currViewHotelsNearLocation,
    moveMapTo,
    trackEvent,
    setCenterCoordsLoaded,
    setMapMoved,
    setInitialSearch,
    setSelectedMarker,
    setCurrSearchMapBound,
    setMapAutocompleteSearchCoordinates,
    setCurrViewHotelsNearLocation,
    fetchInitialHotelAvailability,
  ]);

  useEffect(() => {
    if (
      isWishlistMode &&
      mapRef.current &&
      !currSearchLocation &&
      !searchLocation &&
      searchBoundingBox
    ) {
      mapRef.current.fitBounds(
        new google.maps.LatLngBounds(
          {
            lat: searchBoundingBox.southWest.lat,
            lng: searchBoundingBox.southWest.lon,
          },
          {
            lat: searchBoundingBox.northEast.lat,
            lng: searchBoundingBox.northEast.lon,
          }
        )
      );

      setMapMoved(false);
      setSelectedMarker(undefined);
      setCenterCoordsLoaded(true);
    }
  }, [mapRef.current, searchBoundingBox]);

  const points = useMemo(() => {
    if (!lodgings.length) {
      return [];
    }

    let lodgingsArr;

    // If Premier Collection is enabled, there may be multiple versions of the same lodging (regular vs. premier)
    if (isPremierCollectionEnabled) {
      const lodgingMap = new Map();

      lodgings.forEach((lodging) => {
        const id = lodging.lodging.id;
        if (
          !lodgingMap.has(id) ||
          lodging.lodgingCollection === LodgingCollectionEnum.Premier
        ) {
          lodgingMap.set(id, lodging);
        }
      });

      lodgingsArr = Array.from(lodgingMap.values());

      // Otherwise, just filter out the premier lodgings
    } else {
      lodgingsArr = lodgings.filter(
        (lodging) => lodging.lodgingCollection !== LodgingCollectionEnum.Premier
      );
    }

    // Then reduce the lodgings to the supercluster data structure
    const points: Array<SuperclusterPoint> = lodgingsArr.reduce(
      (acc, lodging, index) => {
        if (!lodging.available) return acc; // Skip unavailable lodgings

        acc.push({
          type: "Feature" as const,
          properties: {
            cluster: false,
            lodging: lodging,
            lodgingRank: index,
          },
          geometry: {
            type: "Point" as const,
            coordinates: [
              lodging.lodging.location.coordinates.lon,
              lodging.lodging.location.coordinates.lat,
            ],
          },
        });

        return acc;
      },
      []
    );

    return points;
  }, [lodgings, isPremierCollectionEnabled]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: showClustersVariant ? 95 : 0, maxZoom: 16 },
  });

  const handleMarkerClick = useCallback(
    (markerId: string, lat: number, lng: number, lodging: Lodging) => {
      setSelectedMarker(markerId);
      setSelectedMarkerLodging(lodging);

      const isHotel = !Boolean(lodging?.homesListingDetails);

      const properties: ClickedLodgingMapProperties = {
        map_funnel: MapFunnelEnum.STAYS,
        click_type: isHotel
          ? ClickedLodgingMapEnum.HOTEL
          : ClickedLodgingMapEnum.VACATION_RENTAL,
        initial_search: initialSearch,
        ...viewedHotelListProperties.properties,
        ...getLodgingTrackingProperties(lodging).properties,
      };

      const encryptedProperties = [
        ...viewedHotelListProperties.encryptedProperties,
        ...getLodgingTrackingProperties(lodging).encryptedProperties,
      ];

      trackEvent({
        eventName: CLICKED_LODGING_MAP,
        properties: properties,
        encryptedProperties: encryptedProperties,
      });

      // Only pan if desktop
      if (matchesDesktop) {
        moveMapTo(lat, lng);
        mapRef.current.panBy(0, -100); // After moving the map, offset the Y position to account for the map card
      }
    },
    [initialSearch, viewedHotelListProperties]
  );

  const handleMarkerHover = useCallback((markerId: string) => {
    setHoveredMarker(markerId);
  }, []);

  const handleMarkerClose = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation(); // TODO: The onClick handlers overlap in the packages component which requires this fix. It should be fixed on the shared component.
      setSelectedMarker(undefined);
    },
    []
  );

  const handleOpenHotelDetailsWithOOPCheck = useCallback(
    (lodging: Lodging) => {
      const hasPolicyCompliance = "corporateTravel" in lodging.lodging;
      const isInPolicy =
        hasPolicyCompliance &&
        ((lodging as CorpLodging).lodging.corporateTravel.policyCompliance
          .isInPolicy ??
          true);

      if (hasPolicyCompliance && !isInPolicy) {
        const modalType = isAutoApprovalEnabled
          ? "out_of_policy_auto"
          : "out_of_policy_24hr_review";
        trackEvent(
          getShowOOPModalEvent(
            ModalScreens.HOTELS_AVAILABILITY,
            "hotels",
            modalType
          )
        );
        const cacheHotelTokenEnabled =
          getExperimentVariant(expState.experiments, CACHE_HOTEL_TOKEN) ===
          AVAILABLE;
        const staysSearchEnabled =
          getExperimentVariant(expState.experiments, STAYS_SEARCH) ===
          AVAILABLE;
        const isLuxuryVRinStays = isLifestyleCollectionEnabled;

        const isHome = lodging.stayType === StayType.Homes;
        const isLuxury =
          lodging.lodgingCollection === LodgingCollectionEnum.Premier ||
          lodging.lodgingCollection === LodgingCollectionEnum.Lifestyle;
        const isLuxuryHome = isHome && isLuxury && isLuxuryVRinStays;

        let params = transformToStringifiedQuery({
          lodgingId: lodging.lodging.id,
          lodgingSelection: mapBounds
            ? {
                LodgingSelection: LodgingSelectionEnum.Location,
                descriptor: mapBounds,
              }
            : (searchLocationResult?.id as IIdLodgings).lodgingSelection,
          ...hotelQueryParams,
          roomsCount,
          fromHotelAvailability: isLuxury ? true : undefined,
          hotelDetailsEntrySource: HotelDetailsEntrySourceEnum.MAP,
          shopToken: cacheHotelTokenEnabled
            ? lodging.price?.opaqueShopRequest
            : localCache.set(uuidv4(), lodging.price?.opaqueShopRequest || "")
                .key,
          sortOrder,
          encodedPersistentLodgingTrackingProperties:
            buildEncodedPersistentLodgingTrackingProperties(lodging),
        });

        if (isHome && staysSearchEnabled) {
          params = params.replace("lodgingId", "listingId");
        }

        const path =
          isHome && staysSearchEnabled && !isLuxuryHome
            ? PATH_VACATION_RENTAL_SHOP
            : isLuxuryHome
            ? PREMIER_PATH_VACATION_RENTALS_SHOP
            : lodging.lodgingCollection === LodgingCollectionEnum.Premier ||
              lodging.lodgingCollection === LodgingCollectionEnum.Lifestyle
            ? PREMIER_COLLECTION_PATH_SHOP
            : PATH_SHOP;

        setShowPolicyModal(`${path}${params}`);
      } else {
        openHotelDetailsPage(
          matchesMobile,
          history,
          lodging,
          expState,
          viewedHotelListProperties,
          mapBounds,
          searchLocationResult,
          roomsCount,
          hotelQueryParams,
          sortOrder
        );
      }
    },
    [
      expState,
      viewedHotelListProperties,
      mapBounds,
      searchLocationResult,
      roomsCount,
      hotelQueryParams,
      sortOrder,
    ]
  );

  const renderMarker = useCallback(
    (point: SuperclusterPoint) => {
      const [longitude, latitude] = point.geometry.coordinates;
      const { lodging, lodgingRank } = point.properties;

      return (
        <ListingMarker
          key={`point-${lodging?.lodging?.id}`}
          zIndex={
            [selectedMarker, hoveredMarker, listHoveredLodgingId].includes(
              lodging?.lodging?.id
            )
              ? Z_INDEX_HOVER_OR_SELECTED_LISTING_MARKER
              : Z_INDEX_BASE_LISTING_MARKER - lodgingRank // Lodging rank is from 1-N where 1 is the highest.
          } // This prop is used by google-maps-react-markers during the rendering of the markers
          lat={latitude}
          lng={longitude}
          lodging={lodging}
          roomsCount={roomsCount}
          isVentureX={isVentureX}
          searchedNightCount={searchedNightCount || 0}
          rewardsKey={accountReferenceId || undefined}
          largestValueAccount={largestValueAccount}
          isSelected={lodging?.lodging?.id === selectedMarker}
          forceHoverState={lodging?.lodging?.id === listHoveredLodgingId}
          disableMapCard={!matchesDesktop} // Hide the map card on mobile
          onMarkerHover={handleMarkerHover} // Updates the z-Index of the marker
          onMarkerClick={(markerId, lat, lng) =>
            handleMarkerClick(markerId, lat, lng, lodging)
          }
          onMarkerClose={handleMarkerClose}
          onMarkerDetailsClick={() => {
            handleOpenHotelDetailsWithOOPCheck(lodging);
          }}
        />
      );
    },
    [
      selectedMarker,
      hoveredMarker,
      listHoveredLodgingId,
      roomsCount,
      expState,
      handleMarkerHover,
      handleMarkerClick,
      handleMarkerClose,
      handleOpenHotelDetailsWithOOPCheck,
    ]
  );

  const handleClusterOnClick = useCallback(
    (clusterId: number, lat: number, lng: number) => {
      let zoom =
        supercluster?.getClusterExpansionZoom(clusterId) || DEFAULT_ZOOM_LEVEL;

      trackEvent({
        eventName: CLICKED_LODGING_MAP,
        properties: {
          map_funnel: MapFunnelEnum.STAYS,
          click_type: ClickedLodgingMapEnum.CLUSTER,
          initial_search: initialSearch,
        },
      });
      moveMapTo(lat, lng, zoom);
    },
    [supercluster]
  );

  const handleClusterHover = useCallback((clusterId: number) => {
    setHoveredCluster(clusterId);
  }, []);

  const renderCluster = useCallback(
    (cluster: SuperclusterCluster) => {
      const [longitude, latitude] = cluster.geometry.coordinates;
      const { cluster_id: id, point_count: pointCount } = cluster.properties;

      const isHoveredListingInCluster = listHoveredLodgingId
        ? supercluster
            ?.getLeaves(id, Infinity, 0)
            .map((point) => point.properties.lodging.lodging.id)
            .includes(listHoveredLodgingId)
        : false;

      const isHoveredCluster = hoveredCluster === cluster.id;

      return (
        <ClusterMarker
          key={`cluster-${id}`}
          zIndex={
            isHoveredCluster
              ? Z_INDEX_HOVER_OR_CLICKED_CLUSTER_MARKER
              : Z_INDEX_CLUSTER_MARKER
          } // This prop is used by google-maps-react-markers during the rendering of the markers
          lat={latitude}
          lng={longitude}
          pointCount={pointCount}
          forceHoverState={isHoveredListingInCluster}
          clusterId={id}
          onClusterHover={handleClusterHover}
          onClusterClick={handleClusterOnClick}
        />
      );
    },
    [supercluster, hoveredCluster, listHoveredLodgingId]
  );

  const renderMarkers = useMemo(() => {
    if (!showClustersVariant) {
      const points = clusters.flatMap((cluster) => {
        if (cluster.properties.cluster) {
          const { cluster_id } = cluster.properties as ClusterProperties;

          return supercluster?.getLeaves(cluster_id) || [];
        } else {
          return cluster as SuperclusterPoint;
        }
      });

      const limitedPoints = points
        .sort((a, b) =>
          a.properties.lodgingRank < b.properties.lodgingRank ? -1 : 1
        )
        .slice(0, noClusteringMarkerLimit);

      return limitedPoints.map(renderMarker);
    } else {
      return clusters
        .flatMap((cluster) => {
          if (cluster.properties.cluster) {
            const { cluster_id, point_count } =
              cluster.properties as ClusterProperties;

            if (point_count < 4) {
              const points = supercluster?.getLeaves(cluster_id) || [];
              return points?.map((point) => {
                return renderMarker(point);
              });
            } else {
              return renderCluster(cluster as SuperclusterCluster);
            }
          } else {
            return renderMarker(cluster as SuperclusterPoint);
          }
        })
        .filter(Boolean);
    }
  }, [
    clusters,
    supercluster,
    zoom,
    showClustersVariant,
    renderCluster,
    renderMarker,
  ]);

  const handleMapOnChange = ({
    zoom,
    bounds,
  }: {
    zoom: number;
    bounds: LatLngBounds;
  }) => {
    setZoom(zoom);
    if (bounds) {
      setBounds([
        bounds.getSouthWest().lng(),
        bounds.getSouthWest().lat(),
        bounds.getNorthEast().lng(),
        bounds.getNorthEast().lat(),
      ]);
    }
  };

  const handleSearchThisArea = () => {
    if (mapRef?.current) {
      setSearchMapBound({
        northEast: {
          lat: mapRef?.current.getBounds().getNorthEast().lat(),
          lon: mapRef?.current.getBounds().getNorthEast().lng(),
        },
        southWest: {
          lat: mapRef?.current.getBounds().getSouthWest().lat(),
          lon: mapRef?.current.getBounds().getSouthWest().lng(),
        },
        LocationDescriptor: LocationDescriptorEnum.BoundingBox,
      });

      trackEvent({
        eventName: SEARCH_THIS_AREA,
        properties: {
          map_funnel: MapFunnelEnum.STAYS,
        },
      });

      fetchInitialHotelAvailability({
        history: history,
        searchFromMap: true,
        searchHotelsNear: false,
        includeHomes: true,
      });
    }
  };

  // Autocomplete value predicate
  const searchNearHereAutocompleteValuePredicate = (
    option: IResult | null,
    value: IResult | null
  ) => {
    const selection = value ? (value.id as IIdLodgings) : null;
    const opt = option ? (option.id as IIdLodgings) : null;
    return (
      !!selection &&
      !!opt &&
      selection.lodgingSelection.placeId === opt.lodgingSelection.placeId
    );
  };

  const renderMapAutocompleteSearchCoordinates = () => {
    const label = viewHotelsNearLocation?.label.split(",")[0];

    if (mapAutocompleteSearchCoordinates && label) {
      const { lat, lng } = mapAutocompleteSearchCoordinates;

      return (
        <LocationMarker
          lat={lat()}
          lng={lng()}
          label={label}
          zIndex={Z_INDEX_VIEW_STAYS_NEAR_MARKER}
        />
      );
    }
    return null;
  };

  const isHotel = !Boolean(selectedMarkerLodging?.homesListingDetails);

  return (
    <>
      {showPolicyModal && (
        <OutOfPolicyModal
          isOpen={!!showPolicyModal}
          isMobile={matchesMobile}
          onClose={() => {
            trackEvent(
              getClickCancelOOPModalEvent(
                ModalScreens.HOTELS_AVAILABILITY,
                "hotels",
                "out_of_policy"
              )
            );
            setShowPolicyModal(null);
          }}
          onContinue={() => {
            trackEvent(
              getClickContinueOOPModalEvent(
                ModalScreens.HOTELS_AVAILABILITY,
                "hotels",
                "out_of_policy"
              )
            );
            if (showPolicyModal) {
              window.open(showPolicyModal, "_blank");
            }
            setShowPolicyModal(null);
          }}
        />
      )}
      <Box
        className="lodging-availability-cluster-map"
        width="100%"
        height="100%"
        position="relative"
      >
        <Skeleton
          variant="rect"
          height="100%"
          animation="wave"
          style={{
            display: initialMapLoaded ? "none" : "block",
          }}
        />

        {/* Map overlays */}
        {!isWishlistMode && initialMapLoaded && (
          <Box>
            {/* View stays near autocomplete */}
            {matchesDesktop && (
              <Box
                position="absolute"
                top="15px"
                left="15px"
                width="300px"
                zIndex={1}
                display="flex"
                alignItems="center"
                onClick={
                  () => setSelectedMarker(undefined) // Reset the selected marker when autocomplete input is focused
                }
              >
                <ViewHotelsNearLocationAutocomplete
                  className="autocomplete-overrides"
                  label={`View ${
                    staysSearchEnabled ? "stays" : "hotels"
                  } near...`}
                  isMobile={false}
                  clearValue={!mapAutocompleteSearchCoordinates}
                  getOptionSelected={searchNearHereAutocompleteValuePredicate}
                  customIcon={
                    <Icon
                      name={IconName.MagnifyingGlass}
                      ariaLabel=""
                      aria-hidden={true}
                    />
                  }
                  endIcon={
                    !mapAutocompleteSearchCoordinates
                      ? undefined
                      : faTimesCircle
                  }
                  endIconOnClick={() => {
                    setMapAutocompleteSearchCoordinates(undefined);
                    fetchViewHotelsNearLocationCategories("");
                  }}
                />
              </Box>
            )}

            {(mapMoved ||
              availabilityCallState !== HotelAvailabilityCallState.Complete ||
              !matchesDesktop) && (
              <Box
                position="absolute"
                display="flex"
                flexDirection="column"
                left="50%"
                bottom="4%"
                zIndex={1}
                alignItems="center"
                style={{
                  transform: "translateX(-50%)",
                }}
              >
                {/* Search this area */}
                {mapMoved && (
                  <Box padding="5px">
                    <ActionButton
                      defaultStyle="h4r-primary"
                      onClick={handleSearchThisArea}
                      message={
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          style={{ gap: "5px" }}
                        >
                          <Icon
                            name={IconName.MagnifyingGlass}
                            className="icon-white-fill"
                          />
                          <Typography
                            style={{ fontSize: "14px", color: "var(--white)" }}
                          >
                            Search this area
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}

                {availabilityCallState !==
                  HotelAvailabilityCallState.Complete && (
                  <Box display="flex" alignItems="center" padding="5px">
                    <Button
                      style={{
                        backgroundColor: "white",
                        borderRadius: "50px",
                        padding: "10px 15px",
                        border: "0.5px solid #CECFCD",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="center"
                        style={{ gap: "8px" }}
                      >
                        <Typography style={{ fontSize: "14px" }}>
                          Loading more stays
                        </Typography>
                        <CircularProgress size="18px" />
                      </Box>
                    </Button>
                  </Box>
                )}

                {!matchesDesktop && (
                  <Box
                    padding={"5px"}
                    width="350px"
                    display="flex"
                    alignItems="center"
                    onClick={() => setViewHotelsNearMobileSearchOpened(true)}
                  >
                    <ViewHotelsNearLocationAutocomplete
                      className={clsx("autocomplete-overrides", {
                        "disable-pointer-events": !matchesDesktop,
                      })}
                      label={`View ${
                        staysSearchEnabled ? "stays" : "hotels"
                      } near...`}
                      isMobile={true} // If true, this component does nothing. It is only used to show the input
                      clearValue={!mapAutocompleteSearchCoordinates}
                      getOptionSelected={
                        searchNearHereAutocompleteValuePredicate
                      }
                      customIcon={
                        <Icon
                          name={IconName.MagnifyingGlass}
                          ariaLabel=""
                          aria-hidden={true}
                        />
                      }
                      endIcon={
                        !mapAutocompleteSearchCoordinates
                          ? undefined
                          : faTimesCircle
                      }
                      endIconOnClick={() => {
                        setMapAutocompleteSearchCoordinates(undefined);
                        fetchViewHotelsNearLocationCategories("");
                        setViewHotelsNearLocation(null);
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {!matchesDesktop && selectedMarkerLodging && (
          <Box
            className="map-card-overrides mobile-map-card-overrides"
            position="absolute"
            bottom="0px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            borderRadius="9px 9px 0px 0px"
            zIndex="2"
            width="100%"
            height="auto"
            style={{
              backgroundColor: "var(--white)",
            }}
          >
            <Box
              className={clsx({
                "premier-collection": shouldShowPcStyling(
                  selectedMarkerLodging
                ),
                "lifestyle-collection": shouldShowLcStyling(
                  selectedMarkerLodging
                ),
              })}
              width="100%"
            >
              {shouldShowPcStyling(selectedMarkerLodging) && (
                <Icon name={IconName.PremierCollectionRibbon} />
              )}
              {shouldShowLcStyling(selectedMarkerLodging) && (
                <Icon name={IconName.LifestyleCollectionRibbon} />
              )}
              <HotelAvailabilityMapTooltip
                lodging={selectedMarkerLodging}
                onCloseClick={(e) => {
                  e.stopPropagation();
                  setSelectedMarkerLodging(undefined);
                }}
                onClickHotelDetails={() =>
                  handleOpenHotelDetailsWithOOPCheck(selectedMarkerLodging)
                }
                nightCount={searchedNightCount ?? 1}
                rewardsKey={accountReferenceId || undefined}
                isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
                isVentureX={isVentureX}
                largestValueAccount={largestValueAccount}
                onViewCarouselImage={() => {
                  trackEvent({
                    eventName: HOTEL_LIST_MAP_CAROUSEL,
                    properties: {
                      map_funnel: MapFunnelEnum.STAYS,
                    },
                  });
                }}
                policyLimit={policies?.hotels.policies[0].maxPricePerNight}
                tenant={config.TENANT}
                searchedRoomsCount={
                  "corporateTravel" in selectedMarkerLodging.lodging
                    ? roomsCount
                    : undefined
                }
                onViewCorpCompareBar={onOpenCompareBarTooltip(
                  ViewedCorpRateDescriptorEntryPoints.HOTELS_LIST_MAP
                )}
                showPolicyBanner={showPolicyBanner}
                isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled && isAllowedPayLaterLodging(allowedPayLaterLodgingIds, selectedMarkerLodging.lodging.id)}
                wishlistComponent={
                  isWishlistExperiment ? (
                    <WishlistToggleHeart
                      listingId={selectedMarkerLodging.lodging.id}
                      listingImageUrl={
                        selectedMarkerLodging.lodging.media?.[0]?.url
                      }
                      stayType={isHotel ? StayType.Hotels : StayType.Homes}
                      lodgingCollection={
                        selectedMarkerLodging.lodgingCollection
                      }
                      style={{
                        position: "absolute",
                        top: "20px",
                        right: "52px",
                        zIndex: 1,
                      }}
                    />
                  ) : undefined
                }
              />
            </Box>
          </Box>
        )}

        {/* Fullscreen takeover to handle mobile variant of "Search near here" */}
        {!isWishlistMode && !matchesDesktop && (
          <MobileViewHotelsNearSearch
            open={viewHotelsNearMobileSearchOpened}
            onClose={() => setViewHotelsNearMobileSearchOpened(false)}
            onComplete={(value) => {
              if (value) {
                setViewHotelsNearLocation(value);
              }
              setViewHotelsNearMobileSearchOpened(false);
            }}
          />
        )}

        <GoogleMap
          apiKey={config.googleMapsApiKey}
          options={{
            clickableIcons: false,
            zoomControl: matchesDesktop ? true : false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            styles: MAP_STYLE,
            gestureHandling: "greedy",
            // @ts-ignore
            cameraControl: false,
          }}
          loadScriptExternally={true}
          status="ready"
          onGoogleApiLoaded={onGoogleApiLoaded}
          errorContent={null}
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM_LEVEL}
          onChange={handleMapOnChange}
          // externalApiParams={{ language: getLang() }} // TODO: Not implemented
        >
          {renderMarkers}
          {mapAutocompleteSearchCoordinates &&
            renderMapAutocompleteSearchCoordinates()}
        </GoogleMap>
      </Box>
    </>
  );
};
